import type { AuditEntryView } from '@ai-sales/shared'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { fetchAudit } from '../api/workspace.api.js'
import { Button } from '../components/button.js'
import { DataTable, EmptyState, type Column } from '../components/data-table.js'
import { ErrorPanel } from '../components/error-panel.js'
import { PageHeader, Panel } from '../components/page.js'
import { formatDateTime, formatRelative } from '../lib/format.js'
import { useCurrentWorkspace } from '../workspace/workspace-context.js'

/**
 * Как назвать действие по-человечески.
 *
 * Код вроде `credential.upsert` точен, но читает журнал не программист,
 * а человек, разбирающий инцидент: «кто снял паузу», «кто поменял ключ».
 * Поэтому на экране стоит фраза, а код прячется в подсказку — он нужен
 * раз в год, при обращении в поддержку.
 *
 * Фразы в прошедшем времени и от третьего лица: строка читается целиком
 * как предложение — «Каролина Ф. изменила роль участника».
 *
 * Неизвестный код показывается как есть. Это лучше «неизвестного
 * действия»: видно, что событие было, даже если перевод ещё не написан.
 */
const ACTION_LABELS: Record<string, string> = {
  'auth.login': 'вошёл в админку',
  'auth.logout': 'вышел из админки',
  'workspace.create': 'создал воркспейс',
  'member.add': 'добавил участника',
  'member.role.update': 'изменил роль участника',
  'member.remove': 'убрал участника',
  'credential.upsert': 'сохранил ключ интеграции',
  'credential.remove': 'удалил ключ интеграции',
  'agent.create': 'создал агента',
  'agent.update': 'переименовал агента',
  'agent.delete': 'удалил агента',
  'agent.version.create': 'сохранил новую версию агента',
  'prompt.create': 'создал промпт',
  'prompt.update': 'переименовал промпт',
  'prompt.delete': 'удалил промпт',
  'prompt.version.create': 'сохранил новую версию промпта',
  'channel.create': 'создал канал',
  'channel.update': 'изменил канал',
  'contact.upsert': 'изменил карточку клиента',
  'conversation.open': 'открыл диалог заново',
  'conversation.closed': 'закрыл диалог',
  'conversation.pause': 'поставил диалог на паузу',
  'conversation.unpause': 'снял паузу с диалога',
  'conversation.handoff': 'позвал человека в диалог',
  'conversation.handoff_clear': 'снял пометку «нужен человек»',
  'conversation.outcome': 'записал, чем кончился разговор',
  'conversation.outcome_clear': 'убрал исход разговора',
  'conversation.outcome_by_agent': 'агент сам закончил разговор',
  'job.diagnostics.enqueue': 'запустил проверку очереди',
  'job_failure.acknowledged': 'взял сбой в работу',
  'job_failure.resolved': 'пометил сбой решённым',
  'job_failure.open': 'вернул сбой в неразобранные',
}

/** Над чем действие совершили. */
const ENTITY_LABELS: Record<string, string> = {
  agent: 'агент',
  channel: 'канал',
  contact: 'клиент',
  conversation: 'диалог',
  credential: 'ключ интеграции',
  job: 'задача',
  job_failure: 'сбой задачи',
  membership: 'участник',
  prompt: 'промпт',
  session: 'вход в админку',
  user: 'пользователь',
  workspace: 'воркспейс',
}

/**
 * Названия полей в снимках «до» и «после».
 *
 * Снимки собираются на сервере из полей базы, и их имена — английские.
 * Показывать их прямо означает требовать от человека знания схемы базы
 * ради ответа на вопрос «что именно поменяли».
 */
const FIELD_LABELS: Record<string, string> = {
  adminApiKey: 'ключ Admin API',
  agentId: 'агент',
  appId: 'идентификатор приложения',
  baseUrl: 'адрес сервиса',
  behaviour: 'поведение задачи',
  bodyLength: 'длина текста',
  description: 'описание',
  email: 'почта',
  hints: 'приметы ключа',
  isBlacklisted: 'чёрный список',
  isPaused: 'пауза',
  jobName: 'задача',
  kind: 'вид',
  label: 'метка',
  name: 'название',
  note: 'заметка',
  outcome: 'исход',
  password: 'пароль',
  pausedAutomatically: 'пауза поставлена сама',
  postbackHost: 'адрес постбэка',
  promptCreatedInline: 'промпт написан тут же',
  provider: 'поставщик',
  published: 'опубликована',
  returnToken: 'ответ трекера',
  reason: 'причина',
  role: 'роль',
  state: 'состояние',
  status: 'состояние',
  system: 'системная инструкция',
  token: 'токен',
  trackerUrl: 'адрес трекера',
  validation: 'проверка ответа',
  version: 'версия',
}

/**
 * Значения, у которых есть человеческое имя.
 *
 * Словарь общий на все поля, а не отдельный на каждое: значения вроде
 * `refused` или `viewer` не пересекаются между собой, и одна таблица
 * читается легче четырёх.
 */
const VALUE_LABELS: Record<string, string> = {
  acknowledged: 'в работе',
  admin: 'администратор',
  agent: 'агент',
  chatapp: 'ChatApp — шлюз WhatsApp',
  closed: 'закрыт',
  fail: 'должна упасть',
  handedToCallCenter: 'передан в колл-центр',
  internal: 'внутренний тестовый',
  keitaro: 'Keitaro — трекер конверсий',
  ok: 'должна пройти',
  openai: 'OpenAI — движок агента',
  operator: 'оператор',
  ordered: 'оформил заказ',
  orderhub: 'OrderHub — заказы',
  owner: 'владелец',
  refused: 'отказался',
  resolved: 'решён',
  viewer: 'наблюдатель',
}

const ENTITY_FILTERS = [
  { value: '', label: 'Все сущности' },
  { value: 'conversation', label: 'Диалоги' },
  { value: 'agent', label: 'Агенты' },
  { value: 'prompt', label: 'Промпты' },
  { value: 'credential', label: 'Ключи интеграций' },
  { value: 'membership', label: 'Участники' },
  { value: 'session', label: 'Входы и выходы' },
  { value: 'channel', label: 'Каналы' },
  { value: 'contact', label: 'Клиенты' },
  { value: 'workspace', label: 'Воркспейс' },
  { value: 'job', label: 'Задачи' },
  { value: 'job_failure', label: 'Сбои задач' },
]

export function AuditPage() {
  const workspace = useCurrentWorkspace()
  const [entityType, setEntityType] = useState('')

  /**
   * Бесконечная подгрузка, а не страницы с номерами. Курсор с сервера
   * указывает на конкретную запись, поэтому «показать ещё» не теряет
   * и не повторяет записи, даже когда журнал пополняется во время чтения.
   */
  const audit = useInfiniteQuery({
    queryKey: ['audit', workspace.id, entityType],
    queryFn: ({ pageParam }) =>
      fetchAudit(workspace.id, {
        limit: 50,
        ...(pageParam ? { cursor: pageParam } : {}),
        ...(entityType ? { entityType } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  })

  const rows = audit.data?.pages.flatMap((page) => page.items) ?? []

  const columns: Column<AuditEntryView>[] = [
    {
      key: 'at',
      header: 'Когда',
      render: (entry) => (
        <span className="text-sm whitespace-nowrap text-ink" title={formatDateTime(entry.at)}>
          {formatRelative(entry.at)}
        </span>
      ),
    },
    {
      key: 'action',
      header: 'Действие',
      render: (entry) => (
        <p className="text-sm text-ink" title={entry.action}>
          {ACTION_LABELS[entry.action] ?? entry.action}
        </p>
      ),
    },
    {
      key: 'actor',
      header: 'Кто',
      render: (entry) => (
        <div className="min-w-0">
          <p className="truncate text-sm text-ink">{entry.actorEmail ?? 'система'}</p>
          {entry.ip && <p className="font-mono text-xs text-muted">{entry.ip}</p>}
        </div>
      ),
    },
    {
      key: 'entity',
      header: 'Объект',
      render: (entry) => (
        <div className="min-w-0">
          <p className="text-sm text-ink-soft">
            {ENTITY_LABELS[entry.entityType] ?? entry.entityType}
          </p>
          {/*
            Опознаватель записи — целиком он нужен только поддержке,
            поэтому на экране короткий хвост, а полное значение
            открывается подсказкой.
          */}
          {entry.entityId && (
            <p className="truncate font-mono text-xs text-muted" title={entry.entityId}>
              {entry.entityId.length > 12 ? `…${entry.entityId.slice(-6)}` : entry.entityId}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'change',
      header: 'Что изменилось',
      render: (entry) => <ChangeCell entry={entry} />,
    },
  ]

  return (
    <>
      <PageHeader
        title="Журнал действий"
        description="Только дописывается, никогда не переписывается. Здесь видно, кто менял ключи, снимал паузу и правил права."
        actions={
          <Button size="sm" loading={audit.isFetching} onClick={() => void audit.refetch()}>
            Обновить
          </Button>
        }
      />

      <div className="flex flex-col gap-4">
        <Panel>
          <label className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted">Показывать:</span>
            <select
              className="h-8 rounded-md border border-line bg-raised px-2 text-sm"
              value={entityType}
              onChange={(event) => setEntityType(event.target.value)}
            >
              {ENTITY_FILTERS.map((filter) => (
                <option key={filter.value} value={filter.value}>
                  {filter.label}
                </option>
              ))}
            </select>
          </label>
        </Panel>

        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(entry) => entry.id}
          isLoading={audit.isPending}
          isRefreshing={audit.isFetching && !audit.isPending && !audit.isFetchingNextPage}
          empty={
            <EmptyState
              title="Записей нет"
              description={
                entityType
                  ? 'По выбранному фильтру ничего не нашлось. Попробуйте показать все сущности.'
                  : 'Журнал пополнится, как только в воркспейсе начнут что-то менять.'
              }
            />
          }
        />

        {audit.error && <ErrorPanel error={audit.error} onRetry={() => void audit.refetch()} />}

        {audit.hasNextPage && (
          <div>
            <Button
              loading={audit.isFetchingNextPage}
              onClick={() => void audit.fetchNextPage()}
            >
              Показать ещё
            </Button>
          </div>
        )}
      </div>
    </>
  )
}

/**
 * Что именно поменялось.
 *
 * Показывается только изменившееся: сравнение снимков даёт список полей,
 * а не два объекта целиком. Иначе на каждой записи оказывается двадцать
 * строк, из которых девятнадцать не менялись, и журнал нечитаем.
 *
 * Стрелка «было → стало» рисуется только когда было чему меняться.
 * Для новой записи прежнего значения нет вовсе, и зачёркнутое «пусто»
 * рядом со значением сбивает с толку, а не помогает.
 */
function ChangeCell({ entry }: { entry: AuditEntryView }) {
  const before = asRecord(entry.before)
  const after = asRecord(entry.after)

  if (!before && !after) {
    return <span className="text-xs text-muted">—</span>
  }

  const keys = [...new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])]
  const changed = keys.filter((key) => stringify(before?.[key]) !== stringify(after?.[key]))

  if (changed.length === 0) {
    return <span className="text-xs text-muted">поля не менялись</span>
  }

  return (
    <dl className="flex flex-col gap-1">
      {changed.slice(0, 6).map((key) => {
        const had = before !== null && key in before && before[key] !== null
        const has = after !== null && key in after

        return (
          <div key={key} className="flex flex-wrap items-baseline gap-1.5 text-xs">
            <dt className="text-muted">{FIELD_LABELS[key] ?? key}</dt>
            {had && <dd className="text-ink-soft line-through">{humanValue(before[key])}</dd>}
            {had && has && <span aria-hidden="true" className="text-muted">→</span>}
            {has && <dd className="font-medium text-ink">{humanValue(after[key])}</dd>}
          </div>
        )
      })}
      {changed.length > 6 && (
        <span className="text-xs text-muted">и ещё {changed.length - 6}</span>
      )}
    </dl>
  )
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

/**
 * Значение так, как его прочитает человек.
 *
 * `true` и `false` сами по себе ничего не значат: в журнале они стоят
 * рядом с названием поля — «пауза: да». Числа и строки остаются как есть,
 * известные коды переводятся, всё незнакомое показывается дословно —
 * соврать переводом хуже, чем показать код.
 */
function humanValue(value: unknown): string {
  if (value === undefined || value === null) return 'пусто'
  if (typeof value === 'boolean') return value ? 'да' : 'нет'
  if (typeof value === 'number') return String(value)

  if (typeof value === 'string') {
    if (value.trim() === '') return 'пусто'
    return VALUE_LABELS[value] ?? value
  }

  if (Array.isArray(value)) {
    return value.length === 0 ? 'пусто' : value.map(humanValue).join(', ')
  }

  const record = asRecord(value)

  if (record) {
    return (
      Object.entries(record)
        .map(([key, item]) => `${FIELD_LABELS[key] ?? key}: ${humanValue(item)}`)
        .join(', ') || 'пусто'
    )
  }

  return String(value)
}

/** Сравнение снимков — по одинаковому представлению, а не по ссылке. */
function stringify(value: unknown): string {
  if (value === undefined) return '—'
  if (value === null) return 'пусто'
  if (typeof value === 'string') return value
  return JSON.stringify(value)
}
